import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { checkoutService } from '@/services/checkout.service'
import { trackUmami } from '@/lib/tracking'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Clock } from 'lucide-react'

export default function ProcessingPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()

  // Poll status da ordem a cada 3 segundos
  const { data: order } = useQuery({
    queryKey: ['order-status', orderId],
    queryFn: () => checkoutService.getOrder(orderId!),
    enabled: !!orderId,
    refetchInterval: 3000, // Poll a cada 3 segundos
  })

  const failureTrackedRef = useRef(false)
  useEffect(() => {
    if (order?.payment_status === 'paid') {
      navigate(`/success/${orderId}`)
    } else if (order && ['failed', 'expired', 'canceled', 'refunded', 'chargedback'].includes(order.payment_status)) {
      if (!failureTrackedRef.current) {
        failureTrackedRef.current = true
        const methodLabels: Record<string, string> = {
          pix: 'PIX',
          boleto: 'Boleto',
          credit_card: 'Cartao de Credito',
        }
        trackUmami('Pagamento Negado', {
          metodo: methodLabels[order.payment_method] || order.payment_method,
          status: order.payment_status,
          pedido: order.order_number,
        })
      }
      navigate('/error', {
        state: {
          error: 'Pagamento não confirmado. Tente novamente.',
        },
      })
    }
  }, [order, orderId, navigate])

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted p-4 py-12">
      <div className="mx-auto max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/20">
              <Clock className="h-8 w-8 animate-pulse text-yellow-600 dark:text-yellow-400" />
            </div>
            <CardTitle>Aguardando Confirmação</CardTitle>
            <CardDescription>
              Estamos verificando seu pagamento...
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Isso pode levar alguns segundos
              </p>
            </div>

            <div className="rounded-lg bg-muted p-4">
              <h4 className="mb-2 font-semibold text-sm">Processando pagamento via:</h4>
              <p className="text-sm text-muted-foreground capitalize">
                {order?.payment_method === 'pix' && 'PIX - Aguardando confirmação automática'}
                {order?.payment_method === 'boleto' && 'Boleto - Aguardando compensação'}
                {order?.payment_method === 'credit_card' && 'Cartão de Crédito'}
              </p>
            </div>

            <div className="space-y-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-950/20">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-400">
                Não feche esta página
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-500">
                Você será redirecionado automaticamente quando o pagamento for confirmado
              </p>
            </div>

            {order && (
              <div className="text-center text-sm text-muted-foreground">
                Pedido: {order.order_number}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
